import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Radio, Spin } from 'antd';
import styled from 'styled-components';

import { getOpenCasesForCustomer } from '../../../../services/framl/landing';
import ManualCaseIdInput from '../../../cm/framl/components/ManualCaseIdInput';
import OpenCaseCardSummary from '../../../cm/framl/components/OpenCaseCardSummary';

// FIN-11360 Merge-to-Case — matches the live mock: a "latest open case per customer"
// quick option, then a per-customer section with a radio list of that customer's OPEN
// cases (C-<id> · Department · Age Nd), latest pre-selected. On Merge, the selected
// alerts of each customer are appended to that customer's chosen case.

const Sub = styled.p`
  font-size: 13px;
  color: #676f7e;
  margin: 0 0 12px;
`;
const QuickBtn = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid #cfe0ff;
  background: #f3f7ff;
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  margin-bottom: 12px;
  & strong {
    display: block;
    font-size: 13px;
    color: #1f2430;
  }
  & span {
    font-size: 12px;
    color: #676f7e;
  }
  &:hover {
    background: #e7f0ff;
  }
`;
const OrPick = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a909c;
  margin: 4px 0 10px;
`;
const Section = styled.div`
  border: 1px solid #e8eaf0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
`;
const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;
const Name = styled.div`
  font-weight: 600;
  font-size: 13px;
  color: #1f2430;
`;
const Meta = styled.div`
  font-size: 11px;
  color: #8a909c;
`;
const Badge = styled.span`
  font-size: 10px;
  border: 1px solid #dfe3ea;
  border-radius: 20px;
  padding: 1px 8px;
  color: #676f7e;
`;
const CaseRow = styled(Radio)`
  display: flex;
  align-items: center;
  border: 1px solid #eef0f4;
  border-radius: 6px;
  padding: 6px 8px;
  margin: 0 0 6px;
  font-weight: normal;
  width: 100%;
`;
const NoCases = styled.div`
  font-size: 12px;
  color: #9aa0aa;
  font-style: italic;
`;
const Note = styled.div`
  font-size: 11px;
  color: #676f7e;
  background: #f6f8fb;
  border-radius: 6px;
  padding: 8px 10px;
  margin-top: 8px;
`;

function FramlMergeToCaseModal({
  open,
  customers,
  onMerge,
  onCancel,
  submitting
}) {
  // customers: [{ customerKey, customerName, alertIds: [] }]
  const [openCases, setOpenCases] = useState({}); // customerKey -> [{caseId, department, ageDays, status, caseName}]
  const [selections, setSelections] = useState({}); // customerKey -> caseId
  // FIN-12957 — customerKey -> manually typed case id (digits-only via
  // ManualCaseIdInput). The customer-detail picker had this; the queue modal
  // didn't, so a case not in the open-cases list was unreachable here.
  const [manual, setManual] = useState({});
  const [loading, setLoading] = useState(false);
  // FIN-12932 — customerKey -> true when the open-cases fetch FAILED. The old catch collapsed
  // 401/404/500 into an empty list, rendering exactly like "No open cases for this customer" —
  // making linkage/env failures undiagnosable from the UI.
  const [fetchErrors, setFetchErrors] = useState({});

  // FIN-12930: the fetch effect keys on a STABLE signature (open + joined customer
  // keys), not the `customers` array reference — the parent rebuilds that array on
  // every auto-refresh tick, and keying on the reference refired this effect and
  // reset the user's radio picks mid-interaction. With the signature, the effect
  // re-runs (and re-initialises selections to each customer's latest open case)
  // only when the modal opens or the SET of customers actually changes.
  const customersSignature = (customers || [])
    .map((c) => c.customerKey)
    .join('|');

  useEffect(() => {
    if (!open || !customers || customers.length === 0) return undefined;
    let active = true;
    setLoading(true);
    Promise.all(
      customers.map((c) =>
        getOpenCasesForCustomer(c.customerKey)
          .then((cases) => [c.customerKey, cases, null])
          // FIN-12932 — keep the error distinct from a genuinely empty list.
          .catch((err) => [c.customerKey, [], err])
      )
    ).then((entries) => {
      if (!active) return;
      const map = {};
      const init = {};
      const errs = {};
      entries.forEach(([key, cases, err]) => {
        map[key] = cases;
        if (err) errs[key] = true;
        if (cases[0]) init[key] = cases[0].caseId; // latest open case (list is latest-first)
      });
      setOpenCases(map);
      setSelections(init);
      setManual({});
      setFetchErrors(errs);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [open, customersSignature]); // eslint-disable-line react-hooks/exhaustive-deps

  // Quick option: pick each customer's latest (most recent) open case.
  // FIN-12930: applies EXPLICITLY via setSelections — meaningful after the user has
  // manually changed a radio (it re-selects the latest per customer). Before the
  // effect fix above it looked dead: every refresh tick reset selections to exactly
  // what this button computes.
  const selectLatestPerCustomer = () => {
    const init = {};
    (customers || []).forEach((c) => {
      const cases = openCases[c.customerKey] || [];
      if (cases[0]) init[c.customerKey] = cases[0].caseId;
    });
    setSelections(init);
  };

  // FIN-12950 semantics (mirrors MergeOpenCasesPicker): manual entry and radio
  // pick are mutually exclusive per customer — typing clears the radio, picking
  // a radio clears the manual input.
  const setSelection = (customerKey, caseId) => {
    setSelections((s) => ({ ...s, [customerKey]: caseId }));
    setManual((m) => ({ ...m, [customerKey]: '' }));
  };

  const setManualId = (customerKey, sanitized) => {
    setManual((m) => ({ ...m, [customerKey]: sanitized }));
    if (sanitized.trim() !== '') {
      setSelections((s) => ({ ...s, [customerKey]: undefined }));
    }
  };

  const effectiveCaseId = (customerKey) =>
    (manual[customerKey] || '').trim() || selections[customerKey];

  const handleMerge = () => {
    const plan = (customers || [])
      .map((c) => ({
        customerKey: c.customerKey,
        alertIds: c.alertIds || [],
        caseId: effectiveCaseId(c.customerKey)
      }))
      .filter((p) => p.caseId && p.alertIds.length > 0);
    onMerge(plan);
  };

  // FIN-13083 — this MUST mirror handleMerge's filter above. It used to gate only on a case
  // being picked, while handleMerge also requires alertIds.length > 0, so for a customer whose
  // every alert is already cased the button was enabled and produced an empty plan: no request,
  // no toast, dialog open. Same shape as the Create Case sibling's createBtnDisabled, which
  // already refuses to submit at zero eligible alerts.
  const anySelected = (customers || []).some(
    (c) => effectiveCaseId(c.customerKey) && (c.alertIds || []).length > 0
  );

  return (
    <Modal
      title="Merge alerts into an existing case"
      visible={open}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="merge"
          type="primary"
          disabled={!anySelected || submitting}
          loading={submitting}
          onClick={handleMerge}>
          Merge
        </Button>
      ]}>
      <Sub>
        Pick an open case per customer. Selected alerts will be appended to the
        chosen case.
      </Sub>

      <QuickBtn type="button" onClick={selectLatestPerCustomer}>
        <strong>Merge to the latest open case per customer</strong>
        <span>
          Automatically appends to each customer&apos;s most recent open case.
        </span>
      </QuickBtn>

      <OrPick>Or pick manually</OrPick>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin />
        </div>
      ) : (
        (customers || []).map((c) => {
          const cases = openCases[c.customerKey] || [];
          return (
            <Section key={c.customerKey}>
              <Header>
                <div>
                  <Name>{c.customerName || c.customerKey}</Name>
                  <Meta>
                    {c.customerKey} · {(c.alertIds || []).length} alert(s)
                  </Meta>
                  {/* FIN-13083 — "0 alert(s)" alone gave no clue why Merge
                      did nothing. Name the case they belong to. */}
                  {(c.alertIds || []).length === 0 &&
                  (c.casedAlerts || []).length > 0 ? (
                    <Meta>
                      No mergeable alerts — already in case{' '}
                      {Array.from(
                        new Set((c.casedAlerts || []).map((a) => a.caseId))
                      ).join(', ')}
                    </Meta>
                  ) : null}
                </div>
                <Badge>{cases.length} open</Badge>
              </Header>
              {cases.length === 0 ? (
                <NoCases>
                  {fetchErrors[c.customerKey]
                    ? "Couldn't load open cases for this customer — close and retry."
                    : 'No open cases for this customer'}
                </NoCases>
              ) : (
                <Radio.Group
                  value={selections[c.customerKey]}
                  onChange={(e) => setSelection(c.customerKey, e.target.value)}
                  style={{ width: '100%' }}>
                  {cases.map((cs) => (
                    <CaseRow
                      key={cs.caseId}
                      value={cs.caseId}
                      title={cs.caseName}>
                      {/* FIN-12954 — the queue card carried no alert count and no status at
                          all, so the two merge surfaces described the same case differently.
                          Both now read the BE's case-scoped count and configured status label.
                          FIN-13003 — and both now render through ONE component, so the layout
                          cannot fork again either. */}
                      <OpenCaseCardSummary openCase={cs} />
                    </CaseRow>
                  ))}
                </Radio.Group>
              )}
              {/* FIN-12957 — manual Case-ID entry per customer (works even when the
                  open-cases list is empty or failed to load). */}
              <div style={{ marginTop: 8 }}>
                <Meta style={{ marginBottom: 4 }}>
                  Or enter a Case ID manually:
                </Meta>
                <ManualCaseIdInput
                  data-testid={`merge-manual-case-id-${c.customerKey}`}
                  value={manual[c.customerKey] || ''}
                  onChange={(sanitized) =>
                    setManualId(c.customerKey, sanitized)
                  }
                  style={{ width: 200 }}
                />
              </div>
            </Section>
          );
        })
      )}

      <Note>
        Note: The final FRAML Score and Fraud heat would be captured during the
        case creation or merging flow.
      </Note>
    </Modal>
  );
}

FramlMergeToCaseModal.propTypes = {
  open: PropTypes.bool,
  // FIN-13958 review R2 — a merge is in flight: Merge is locked so it cannot be sent twice.
  submitting: PropTypes.bool,
  customers: PropTypes.arrayOf(
    PropTypes.shape({
      customerKey: PropTypes.string,
      customerName: PropTypes.string,
      alertIds: PropTypes.array
    })
  ),
  onMerge: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

FramlMergeToCaseModal.defaultProps = {
  open: false,
  customers: [],
  submitting: false
};

export default FramlMergeToCaseModal;
